import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '~/components/button';
import { api } from '~/lib/api';

export const Route = createFileRoute('/_authed/founder/identity-output')({
  component: IdentityOutputPage,
});

function IdentityOutputPage() {
  const [output, setOutput] = useState('');
  const [identity, setIdentity] = useState('');
  const trend = useQuery({ queryKey: ['identity-output-trend'], queryFn: api.identityOutputTrend });

  const submit = useMutation({
    mutationFn: () =>
      api.identityOutputRitual({
        outputLines: output.split('\n').map((s) => s.trim()).filter(Boolean),
        identityLines: identity.split('\n').map((s) => s.trim()).filter(Boolean),
      }),
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">identity vs output</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        weekly ritual. write each list on its own. the system flags conflations honestly.
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            what you did (output)
          </label>
          <textarea
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            rows={8}
            placeholder="one line per output. the project is behind. revenue flat. board deck half done."
            className="mt-2 w-full rounded-[var(--radius)] border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            what you are (identity claims)
          </label>
          <textarea
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            rows={8}
            placeholder="one line per claim. i am tired. i am behind. i'm a fraud."
            className="mt-2 w-full rounded-[var(--radius)] border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      <div className="mt-4">
        <Button disabled={submit.isPending || !identity.trim()} onClick={() => submit.mutate()}>
          {submit.isPending ? 'classifying…' : 'submit ritual'}
        </Button>
      </div>

      {submit.data && (
        <section className="mt-8">
          <h2 className="text-sm uppercase tracking-wide text-muted-foreground">classifier verdicts</h2>
          <ul className="mt-3 space-y-2">
            {submit.data.verdicts.map((v, i) => (
              <li key={i} className="rounded-[var(--radius)] border border-border p-3 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className={v.classification === 'conflation' ? 'font-medium' : ''}>{v.claim}</span>
                  <span
                    className={`text-xs uppercase tracking-wide ${
                      v.classification === 'conflation' ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                  >
                    {v.classification}
                  </span>
                </div>
                {v.reason && <div className="mt-1 text-xs text-muted-foreground">{v.reason}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {trend.data && (
        <section className="mt-12 border-t border-border pt-6">
          <h2 className="text-sm uppercase tracking-wide text-muted-foreground">trend (12 weeks)</h2>
          <dl className="mt-3 grid grid-cols-4 gap-3 text-sm">
            {Object.entries(trend.data.counts).map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs text-muted-foreground">{k}</dt>
                <dd className="text-lg font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </section>
  );
}
